import PenIcon from "~icons/local/pen";
import ClearIcon from "~icons/fa6-solid/x";
import { useState } from "react";
import RowLayout from "./lib/RowLayout";
import { quantityColumnWidth } from "./constants";
import useOperationListContext from "./lib/useOperationListContext";
import useGetCurrentQuoteRevision from "@/lib/SelectedQuoteRevisionIdContext/useGetCurrentQuoteRevision";
import useUpdatePart from "@/lib/queries/useUpdatePart";
import Button from "@/lib/Button";

export default function HeaderRow({ preventEdit }: { preventEdit: boolean }) {
  const { updatePart, isPending } = useUpdatePart();
  const { quantities } = useOperationListContext();
  const [editing, setEditing] = useState(false);
  const [quantitiesInEdit, setQuantitiesInEdit] = useState(quantities);
  const [input, setInput] = useState("");
  const canEditQuoteRevision = useGetCurrentQuoteRevision()?.can_edit;

  if (editing) {
    return (
      <div className="w-full py-2 flex gap-1 justify-center text-xs bg-slate-100">
        <div className="flex divide-x divide-slate-100 text-xs overflow-x-auto bg-white rounded-sm">
          {quantitiesInEdit.map((q, i) => (
            <div
              key={i}
              className="flex justify-center items-center gap-2"
              style={{
                maxWidth: quantityColumnWidth,
                minWidth: quantityColumnWidth,
              }}
            >
              {q}
              <ClearIcon
                className="cursor-pointer text-slate-400 hover:text-slate-600 size-3"
                onClick={() =>
                  setQuantitiesInEdit((pre) => pre.filter((n) => n !== q))
                }
              />
            </div>
          ))}
        </div>
        <input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="w-[76px] px-1 bg-white"
          placeholder="add quantity"
          onKeyUp={(e) => {
            if (e.key === "Enter") addQuantity();
            else if (e.key === "Escape") setInput("");
          }}
          onBlur={addQuantity}
        />
        <Button
          primary
          size="sm"
          loading={isPending}
          onClick={() => {
            updatePart({
              payload: { requested_quantities: quantitiesInEdit },
              onSuccess: () => {
                setEditing(false);
              },
            });
          }}
          className="cursor-pointer px-2 bg-blue-600 text-white hover:bg-blue-500 rounded-sm"
        >
          Done
        </Button>
      </div>
    );
  }

  return (
    <RowLayout
      left={<Left />}
      right={<Right />}
      actions={
        preventEdit || !canEditQuoteRevision ? null : (
          <PenIcon
            className="cursor-pointer"
            onClick={() => setEditing(true)}
          />
        )
      }
      className="sticky top-0 z-10"
    />
  );

  function Left() {
    return (
      <div className="w-full px-2 flex justify-between items-center">
        <div className="font-bold">Operations</div>
        <div className="text-sm">Quantities</div>
      </div>
    );
  }

  function Right() {
    return (
      <div className="flex divide-x divide-slate-100 text-sm">
        {quantities.map((q, i) => (
          <div
            key={i}
            className="flex justify-center items-center"
            style={{
              maxWidth: quantityColumnWidth,
              minWidth: quantityColumnWidth,
            }}
          >
            {q}
          </div>
        ))}
      </div>
    );
  }

  function addQuantity() {
    const n = parseInt(input, 10);
    if (n && !quantitiesInEdit.includes(n)) {
      setQuantitiesInEdit((pre) =>
        [...pre, parseInt(input, 10)].sort((a, b) => a - b),
      );
      setInput("");
    }
  }
}
